import _ from 'lodash';

/**
 * One place to store all the symbolic constants used in our components.
 */
export default class Constants {
  //letting constants match JSON so that they can be used to reference the element in the JSON as well
  static TYPE_COORDINATE_SYSTEM = 'coordinate_system';
  static TYPE_INPUT_DATASET = 'input_dataset';
  static TYPE_OUTPUT_DATASTREAM = 'output_datastream';
  static TYPE_RETRIEVED_VARIABLE = 'retrieved_variable';

  static LEVEL_DISALLOW = 'disallow'; // disallow errors prevent the process from being saved
  static LEVEL_ERROR = 'error';
  static LEVEL_WARNING = 'warning';
  static LEVEL_IGNORE = 'ignore';
  static ERROR_LABELS_TO_VALUE = {
    ignore: 0,
    warning: 1,
    error: 2,
    disallow: 3,
  };

  static RETRIEVE_MODE_DS = 'Datastream';
  static RETRIEVE_MODE_REC = 'Recommended Variable';

  static DATA_TYPES = [
    {value: 'char', label: 'char'},
    {value: 'byte', label: 'byte'},
    {value: 'short', label: 'short'},
    {value: 'int', label: 'integer'},
    {value: 'float', label: 'float'},
    {value: 'double', label: 'double'},
    {value: null, label: 'Same as input datastream'},
  ];

  static PROCESS_TYPE_VAP = 'VAP';
  static PROCESS_TYPE_INGEST = 'Ingest';

  static DEFAULT_COORDINATE_SYSTEM_NAME = 'default_coordinate_system';

  static COORDINATE_SYSTEM_DATA_TYPES = Constants.DATA_TYPES.concat([
    {value: 'SAME_AS_RETRIEVED_VARIABLE', label: 'Same as retrieved variable'},
  ]);

  static COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING = 'mapping';
  static COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID = 'regular_grid';
  static COORDINATE_SYSTEM_GRIDDING_METHOD_SET_AT_RUNTIME = 'set_at_runtime';

  static COORDINATE_SYSTEM_GRIDDING_METHODS = [
    {
      value: Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING,
      label: 'Mapping',
    },
    {
      value: Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID,
      label: 'Regular Interval',
    },
    {
      value: Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_SET_AT_RUNTIME,
      label: 'Set at Runtime',
    },
  ];

  static TRANSFORM_TYPE_AUTO = 'AUTO';
  static TRANSFORM_TYPE_AVERAGE = 'TRANS_BIN_AVERAGE';
  static TRANSFORM_TYPE_INTERPOLATE = 'TRANS_INTERPOLATE';
  static TRANSFORM_TYPE_SUBSAMPLE = 'TRANS_SUBSAMPLE';
  static TRANSFORM_TYPE_PASSTHROUGH = 'TRANS_PASSTHROUGH';

  static RANGE_DEFAULT = 'LENGTH_OF_PROCESSING_INTERVAL';

  static INPUT_DATASET_AUTO = 'AUTO-SELECT';

  // Variable overrides can't pick "AUTO" as the transform parameter
  static VARIABLE_OVERRIDE_TRANSFORM_TYPES = [
    {value: Constants.TRANSFORM_TYPE_AVERAGE, label: 'Bin Averaging'},
    {
      value: Constants.TRANSFORM_TYPE_INTERPOLATE,
      label: 'Bilinear Interpolation',
    },
    {
      value: Constants.TRANSFORM_TYPE_SUBSAMPLE,
      label: 'Nearest Neighbor (i.e., subsample)',
    },
    {
      value: Constants.TRANSFORM_TYPE_PASSTHROUGH,
      label: 'Passthrough (i.e., no change in values)',
    },
  ];

  static COORDINATE_SYSTEM_TRANSFORM_TYPES = [
    {value: Constants.TRANSFORM_TYPE_AUTO, label: 'Auto (average/interpolate)'},
    ...Constants.VARIABLE_OVERRIDE_TRANSFORM_TYPES,
  ];

  static COORDINATE_SYSTEM_BIN_ALIGNMENTS = [
    {value: 'MIDDLE', label: 'Center'},
    {value: 'START', label: 'Start'},
    {value: 'END', label: 'End'},
  ];

  static OUTPUT_INTERVAL_OPTIONS = [
    {value: 'hourly', label: 'Hourly'},
    {value: 'daily', label: 'Daily'},
    {value: 'monthly', label: 'Monthly'},
    {value: 'yearly', label: 'Yearly'},
    {value: 'same', label: 'Same as input processing interval'},
    {
      value: 'always',
      label: 'Always split output file when new dataset is stored',
    },
    {value: 'never', label: 'Never split output file'},
  ];

  static OUTPUT_INTERVAL_TIMEZONE_OPTIONS = [
    {value: 'utc', label: 'UTC'},
    {value: 'local', label: 'Local'},
  ];

  static UNITLESS = 'unitless';
  static EPOCH_TIME = 'seconds since 1970-1-1 0:00:00 0:00';
  static UNIT_SECONDS_LABEL = 'seconds';
  static SAME_AS_RETRIEVED_VARIABLE = 'SAME_AS_RETRIEVED_VARIABLE';
  static SAME_AS_INPUT_DATASTREAM = 'SAME_AS_INPUT_DATASTREAM';

  // These are units provided in the combo box, so they don't need to be validated
  static NON_VALIDATED_UNITS = [
    Constants.UNITLESS,
    Constants.EPOCH_TIME,
    Constants.SAME_AS_INPUT_DATASTREAM,
    Constants.SAME_AS_RETRIEVED_VARIABLE,
  ];

  static UNITS = [
    {value: Constants.UNITLESS, label: Constants.UNITLESS},
    {value: Constants.EPOCH_TIME, label: Constants.EPOCH_TIME},
  ];

  static COORDINATE_SYSTEM_UNITS = [
    ...Constants.UNITS,
    {
      value: Constants.SAME_AS_RETRIEVED_VARIABLE,
      label: 'Same as retrieved variable',
    },
  ];

  static RETRIEVED_VARIABLE_UNITS = [
    ...Constants.UNITS,
    {
      value: Constants.SAME_AS_INPUT_DATASTREAM,
      label: 'Same as input datastream',
    },
  ];

  static COORDINATE_SYSTEM_NEW = {
    name: '',
    dimensions: {
      time: {
        name: 'time',
        index: 0,
        coordinate_variable_data_type: 'SAME_AS_RETRIEVED_VARIABLE',
        coordinate_variable_units: 'SAME_AS_RETRIEVED_VARIABLE',
        gridding_method: Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING,
        input_dataset: null,
        coordinate_variables_per_rule: [['time']],
        data_transformation: {
          transformation_type: 'AUTO',
          bin_alignment: 'MIDDLE',
          length: 0,
          start: 0,
          interval: 0,
          end: 0,
        },
      },
    },
  };

  static DIMENSION_NEW_TIME = {
    name: '',
    coordinate_variable_data_type: 'SAME_AS_RETRIEVED_VARIABLE',
    coordinate_variable_units: 'SAME_AS_RETRIEVED_VARIABLE',
    gridding_method: Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING,
    input_dataset: null,
    coordinate_variables_per_rule: [['time']],
    data_transformation: {
      transformation_type: 'AUTO',
      bin_alignment: 'MIDDLE',
      length: 0,
      start: 0,
      interval: 0,
      end: 0,
    },
  };

  static DIMENSION_DEFAULT_TRANSFORMATION = {
    transformation_type: 'AUTO',
    bin_alignment: 'MIDDLE',
    length: 0,
    start: 0,
    interval: 0,
    end: 0,
  };

  static DIMENSION_NEW_OTHER = {
    name: '',
    coordinate_variable_data_type: 'SAME_AS_RETRIEVED_VARIABLE',
    coordinate_variable_units: 'SAME_AS_RETRIEVED_VARIABLE',
    gridding_method: Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING,
    input_dataset: null,
    coordinate_variables_per_rule: [],
    data_transformation: {
      transformation_type: 'TRANS_PASSTHROUGH',
    },
  };

  static INPUT_DATASET_NEW = {
    name: '',
    offsets: [0, 0],
    rules: [],
  };

  static INPUT_DATASET_RULE_NEW = {
    priority: 1,
    run_location: {site: 'RUN_LOCATION', fac: null},
    run_time: {start: 'ANY_TIME', end: 'ANY_TIME'},
    data_location: {site: 'RUN_LOCATION', fac: null},
    datastream_name: '',
    variable_mappings: {},
  };

  static RETRIEVED_VARIABLE_NEW = {
    name: '',
    data_type: 'float',
    units: Constants.SAME_AS_INPUT_DATASTREAM,
    variable_required: 1,
    retrieve_qc_variable: 1,
    qc_variable_required: 0,
    input_dataset: '',
    coordinate_system: null,
    dims: [],
  };

  static PROCESS_WIZARD_NEW_NAME = 'enter_process_name';

  // Empty project structure
  static PROCESS_NEW = {
    name: '',
    type: Constants.PROCESS_TYPE_INGEST,
    processing_interval: '86400',
    output_interval: {},
    description: '',
    notification_email: '',
    email_errors: true,
    email_warnings: false,
    run_locations: [],
    input_datastreams: [],
    output_datastreams: [],
    variable_retrieval: null,
    ui_state: {
      data_locations: _.cloneDeep(Constants.DATA_LOCATIONS_NEW),
    },
  };

  static DATA_LOCATIONS_NEW = {
    user_folder: null,
    use_user: false,
    use_archive: true,
    use_test: false,
  };

  static FILE_TYPE_COORDINATE_SYSTEM = 'coordinate_system';
  static FILE_TYPE_INPUT_DATASTREAM = 'input_datastream';

  static TRANSFORMATION_PARAMETERS = {
    transformation_type: {
      file_type: Constants.FILE_TYPE_COORDINATE_SYSTEM,
      default_value: null,
      dimension_specific: true,
      display_default_init: true,
    },
    range: {
      file_type: Constants.FILE_TYPE_INPUT_DATASTREAM,
      default_value: Constants.RANGE_DEFAULT,
      dimension_specific: true,
      display_default_init: true,
    },
    width: {
      file_type: Constants.FILE_TYPE_COORDINATE_SYSTEM,
      default_value: null,
      dimension_specific: true,
      display_default_init: false,
    },
    missing_value: {
      file_type: Constants.FILE_TYPE_INPUT_DATASTREAM,
      default_value: -9999,
      dimension_specific: false,
      display_default_init: true,
      explore_data_relevance: ['values', 'missing_value'],
      explore_data_default_value: ['values', 'missing_value'],
    },
    qc_mask: {
      file_type: Constants.FILE_TYPE_INPUT_DATASTREAM,
      default_value: null,
      dimension_specific: false,
      display_default_init: true,
      explore_data_relevance: ['qc', 'indeterminate_values'],
      explore_data_default_value: ['qc', 'bad_values'],
    },
    qc_bad: {
      file_type: Constants.FILE_TYPE_INPUT_DATASTREAM,
      default_value: null,
      dimension_specific: false,
      display_default_init: true,
      explore_data_relevance: ['qc', 'indeterminate_values'],
      explore_data_default_value: ['qc', 'bad_values'],
    },
    std_ind_max: {
      file_type: Constants.FILE_TYPE_COORDINATE_SYSTEM,
      default_value: null,
      dimension_specific: true,
      display_default_init: false,
    },
    std_bad_max: {
      file_type: Constants.FILE_TYPE_COORDINATE_SYSTEM,
      default_value: null,
      dimension_specific: true,
      display_default_init: false,
    },
    goodfrac_ind_min: {
      file_type: Constants.FILE_TYPE_COORDINATE_SYSTEM,
      default_value: 0.8,
      dimension_specific: true,
      display_default_init: false,
    },
    goodfrac_bad_min: {
      file_type: Constants.FILE_TYPE_COORDINATE_SYSTEM,
      default_value: 0.5,
      dimension_specific: true,
      display_default_init: false,
    },
  };

  static INPUT_QC_PARAMETERS = ['qc_bad', 'qc_mask', 'missing_value'];

  static TRANSFORM_PARAMETER_NEW = {
    name: '',
    file_type: '',
    coordinate_system_defaults: [],
    input_datastream_defaults: [],
    variables: [],
  };

  static VARIABLE_RETRIEVAL_NEW = {
    input_datasets: {},
    retrieved_variables: {},
    coordinate_systems: {},
    output_datastream_variable_mappings: {},
    transformation_parameters: {},
  };

  static SECONDS_IN_HOUR = 3600;
  static SECONDS_IN_DAY = 86400;
  static SECONDS_IN_WEEK = 604800;

  static RUN_LOCATION_LABEL = 'Any sites/facilities';
  static DATA_LOCATION_LABEL = 'Same location as command line';

  static PERMISSIONS_ERROR_MESSAGE =
    'You are not logged in or do not have permission to edit this process.';
  static DEPRECATED_DISABLED_MESSAGE =
    'This process has been set to a deprecated state and can no longer be edited. To update this state, submit a ticket to request that the process no longer be deprecated.';

  static CREATE_ADI_PROJECT_OPTIONS = {
    adc_folder: 0,
    tarball: 1,
  };

  static SGP_DATASTREAMS_WITH_NO_C1_DATA = [
    'aosapssidescatter.a0',
    'met.b1',
    '5ebbr.a1',
    'aosccn2cola.b1',
    '1440smos.b1',
    'aosccn200.a1',
    '5ebbr.b1',
    '1smos.b1',
    '1swfanalsiros1long.c1',
    'aoscpcf1m.b1',
    'aosso2.b1',
    'aoscaps3w.b1',
    'swats.a1',
    'siros1dutt.c1',
    'aoscpcf.b1',
    'aosaps.a1',
    'aoscpcuf.a1',
    'siros.a1',
    '30baebbr.s1',
    '15ebbr.b1',
    '5ebbr.a0',
    'aossmps.a1',
    'aosso2.a1',
    'aosmet.a1',
    'aosccn2colaavg.b1',
    'smos.b1',
    'aostap.b1',
    'aosccn2colaspectra.b1',
    '15swfanalsiros1long.c1',
    'aosnanosmps.a1',
    'aosimpactor.a1',
    '30smos.b1',
    'aosccn2colb.b1',
    '1smos.a0',
    'aosapstof.a0',
    '30baebbr.c1',
    'irt200ms.a0',
    'irt200ms.a1',
    'aoscpcuf.b1',
    'aospsap3w1s.b1',
    'stamppcp.b1',
    'aoshtdma.a1',
    '30ebbr.b1',
    'aoscpcf.a1',
    'aosuhsas.a1',
    'stamp.b1',
    'aoscaps3w.a1',
    'mfrsraux.a0',
    'swats.b1',
    'smos.a0',
    'aostap.a0',
  ];

  static VAP_TEMPLATE_LIST = [
    {
      key: '1',
      template_name: 'vap-py-transform',
      language: 'Python',
      description:
        'Standard VAP template for processes that need a transformation. Used if one or more of the input datastreams maps to an output datastream with a different coordinate system.',
    },
    {
      key: '2',
      template_name: 'vap-c-transform',
      language: 'C',
      description:
        'Standard VAP template for processes that need a transformation. Used if one or more of the input datastreams maps to an output datastream with a different coordinate system.',
    },
    {
      key: '3',
      template_name: 'vap-py-retriever',
      language: 'Python',
      description:
        'Standard VAP template for processes that do not need a transformation. Used if all input datastreams have the same coordinate system and map to an output datastream with the same coordinate system.',
    },
    {
      key: '4',
      template_name: 'vap-c-retriever',
      language: 'C',
      description:
        'Standard VAP template for processes that do not need a transformation. Used if all input datastreams have the same coordinate system and map to an output datastream with the same coordinate system.',
    },
  ];

  static INGEST_TEMPLATE_LIST = [
    {
      key: '1',
      template_name: 'ingest-c-standard',
      language: 'C',
      description: 'Standard ingest template.',
    },
    {
      key: '2',
      template_name: 'ingest-c-csv',
      language: 'C',
      description: 'Standard ingest template',
    },
    {
      key: '3',
      template_name: 'ingest-c-csv2',
      language: 'C',
      description:
        'Ingest template for CSV input. Used for one input file that maps to one output datastream.',
    },
    {
      key: '4',
      template_name: 'ingest-py',
      language: 'Python',
      description:
        'Ingest template for csv input. Used for multiple input files that map to multiple output datastreams.',
    },
  ];

  static TEMPLATE_TABLE_COLUMNS = [
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
    },
    {
      title: 'Language',
      dataIndex: 'language',
      key: 'language',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
  ];

  /**
   * Use this method to look up an object from an RIE options array.  Used
   * for populating RIEComboBox values based on the data stored in the object model.
   * @param propertyId
   * @param propertiesList
   * @returns {*}
   */
  static getOptionById(propertyId, propertiesList) {
    let property = null;
    for (let i = 0; i < propertiesList.length; i++) {
      if (
        propertiesList[i].id === propertyId ||
        propertiesList[i].value === propertyId
      ) {
        property = propertiesList[i];
        break;
      }
    }
    return property;
  }

  static convertArrayToSelectOptions(valueArray) {
    return valueArray.map((value) => {
      return {value, label: value};
    });
  }

  static convertValueToSelectValue(value, type) {
    const valueObj = {value, label: value};
    if (value === 'RUN_LOCATION') {
      valueObj.label = this.getRunLocationLabel(type);
    }
    return valueObj;
  }

  static sortSelectOptions(optionsArray) {
    return optionsArray.sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
      let compareResult = 0;
      if (aLabel > bLabel) {
        compareResult = 1;
      } else if (bLabel > aLabel) {
        compareResult = -1;
      }
      return compareResult;
    });
  }

  static getRunLocationLabel(type) {
    let label = '';
    if (type === 'runLocation') {
      label = this.RUN_LOCATION_LABEL;
    } else if (type === 'dataLocation') {
      label = this.DATA_LOCATION_LABEL;
    }
    return label;
  }

  static getSecondsDescriptiveLabel = (seconds) => {
    let label = '';
    if (seconds % Constants.SECONDS_IN_WEEK === 0) {
      const weeks = seconds / Constants.SECONDS_IN_WEEK;
      label += weeks === 1 ? ' (every week)' : ` (every ${weeks} weeks)`;
    } else if (seconds % Constants.SECONDS_IN_DAY === 0) {
      const days = seconds / Constants.SECONDS_IN_DAY;
      label += days === 1 ? ' (every day)' : ` (every ${days} days)`;
    } else if (seconds % Constants.SECONDS_IN_HOUR === 0) {
      const hours = seconds / Constants.SECONDS_IN_HOUR;
      label += hours === 1 ? ' (every hour)' : ` (every ${hours} hours)`;
    }
    return label;
  };
}



// WEBPACK FOOTER //
// ./src/utils/Constants.js